import { useRef, useState } from "react";
import { get, send } from "../api.js";
import { useAsync } from "../useAsync.js";
import "./groupActivities.css";

type OpenActivity = {
  id: string;
  title: string;
  activityType: "group_lesson" | "workshop" | "ensemble" | "other";
  description: string | null;
  deliveryMode: "in_person" | "online";
  venue: string | null;
  startsAt: string;
  endsAt: string;
  eligibilityNotes: string | null;
  capacity: number;
  remainingSeats: number;
  waitlistPromotionPolicy: "automatic_reservation" | "manual_confirmation_required";
};

type FamilyActivityRecord = {
  id: string;
  activityId: string;
  learnerId: string;
  learnerDomain: "private" | "school";
  status: string;
  learnerName: string;
  activityTitle: string;
  startsAt: string;
  endsAt: string;
  promotionPolicy?: "automatic_reservation" | "manual_confirmation_required";
};

type Items<T> = { items: T[] };
type ActivitySnapshot = {
  open: OpenActivity[];
  enrolments: FamilyActivityRecord[];
  waitlist: FamilyActivityRecord[];
};

async function loadActivities(): Promise<ActivitySnapshot> {
  const [open, enrolments, waitlist] = await Promise.all([
    get<Items<OpenActivity>>("/group-activities/open"),
    get<Items<FamilyActivityRecord>>("/group-activities/me/enrolments"),
    get<Items<FamilyActivityRecord>>("/group-activities/me/waitlist"),
  ]);
  return { open: open.items, enrolments: enrolments.items, waitlist: waitlist.items };
}

const dateTime = new Intl.DateTimeFormat("en-GB", {
  weekday: "long",
  day: "numeric",
  month: "long",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  timeZone: "Europe/London",
});

function displayRange(startsAt: string, endsAt: string): string {
  return dateTime.format(new Date(startsAt)) + " – " + dateTime.format(new Date(endsAt));
}

function activityTypeLabel(activityType: OpenActivity["activityType"]): string {
  if (activityType === "group_lesson") return "Group lesson";
  if (activityType === "workshop") return "Workshop";
  if (activityType === "ensemble") return "Ensemble";
  return "Activity";
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "That change could not be saved.";
}

export function GroupActivities({
  learnerId,
  learnerName,
}: {
  learnerId: string;
  learnerName: string;
}) {
  const snapshot = useAsync(loadActivities, [learnerId]);
  const [busyActivityId, setBusyActivityId] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<{
    activityId: string;
    kind: "enrolment" | "waitlist";
  } | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const waitlistRequestKeys = useRef(new Map<string, string>());

  const data = snapshot.data;
  const enrolments = (data?.enrolments ?? []).filter((entry) =>
    entry.learnerId === learnerId && entry.status === "enrolled"
  );
  const familyWaitlist = (data?.waitlist ?? []).filter((entry) => entry.learnerId === learnerId);
  const waiting = familyWaitlist.filter((entry) => entry.status === "waiting");
  const invalidated = familyWaitlist.filter((entry) => entry.status === "ineligible");
  const enrolledActivityIds = new Set(enrolments.map((entry) => entry.activityId));
  const waitingActivityIds = new Set(waiting.map((entry) => entry.activityId));

  async function enrol(activity: OpenActivity) {
    setBusyActivityId(activity.id);
    setProblem(null);
    setNotice(null);
    try {
      await send<unknown>(
        "POST",
        "/group-activities/" + encodeURIComponent(activity.id) + "/enrolments",
        { learnerId, idempotencyKey: window.crypto.randomUUID() },
      );
      setNotice("A place has been reserved for " + learnerName + ".");
    } catch (error) {
      setProblem(errorMessage(error));
    } finally {
      snapshot.reload();
      setBusyActivityId(null);
    }
  }

  async function joinWaitlist(activity: OpenActivity) {
    setBusyActivityId(activity.id);
    setProblem(null);
    setNotice(null);
    const requestId = learnerId + ":" + activity.id;
    const idempotencyKey = waitlistRequestKeys.current.get(requestId) ?? window.crypto.randomUUID();
    waitlistRequestKeys.current.set(requestId, idempotencyKey);
    try {
      const queued = await send<FamilyActivityRecord>(
        "POST",
        "/group-activities/" + encodeURIComponent(activity.id) + "/waitlist",
        { learnerId, idempotencyKey },
      );
      waitlistRequestKeys.current.delete(requestId);
      setNotice(
        queued.promotionPolicy === "automatic_reservation"
          ? "If a place opens, " + learnerName + " will be given the place automatically and enrolled without asking you again."
          : "The wait-list request is saved. No place is reserved yet. The organiser may reserve a place for " + learnerName + " later and send you a Hub notification; you can withdraw if you no longer want it.",
      );
    } catch (error) {
      setProblem(errorMessage(error));
    } finally {
      snapshot.reload();
      setBusyActivityId(null);
    }
  }

  async function withdraw(activityId: string, kind: "enrolment" | "waitlist") {
    setBusyActivityId(activityId);
    setProblem(null);
    setNotice(null);
    try {
      const suffix = kind === "enrolment"
        ? "/enrolments/" + encodeURIComponent(learnerId)
        : "/waitlist/" + encodeURIComponent(learnerId);
      await send<unknown>(
        "DELETE",
        "/group-activities/" + encodeURIComponent(activityId) + suffix,
      );
      setConfirmation(null);
      const automaticPromotionEnabled = data?.open.find((activity) => activity.id === activityId)
        ?.waitlistPromotionPolicy === "automatic_reservation";
      setNotice(
        kind === "enrolment"
          ? automaticPromotionEnabled
            ? "The place has been withdrawn. The next eligible waiting learner may now have a place reserved automatically."
            : "The place has been withdrawn. The organiser may offer it to the next waiting family."
          : "The wait-list request has been withdrawn.",
      );
    } catch (error) {
      setProblem(errorMessage(error));
    } finally {
      snapshot.reload();
      setBusyActivityId(null);
    }
  }

  function renderWithdrawalControls(activityId: string, kind: "enrolment" | "waitlist", title: string) {
    const automaticPromotionEnabled = data?.open.find((activity) => activity.id === activityId)
      ?.waitlistPromotionPolicy === "automatic_reservation";
    const confirming = confirmation?.activityId === activityId && confirmation.kind === kind;
    if (confirming) {
      return (
        <div className="group-activity-confirm">
          {kind === "enrolment" && (
            <p>
              {automaticPromotionEnabled
                ? "If you withdraw, the next eligible waiting learner may have the free place reserved automatically."
                : "Withdrawing frees the place. The organiser may offer it to the next waiting family."}
            </p>
          )}
          <button
            type="button"
            disabled={busyActivityId !== null}
            onClick={() => void withdraw(activityId, kind)}
          >
            Confirm withdrawal
          </button>
          <button
            type="button"
            className="quiet"
            disabled={busyActivityId !== null}
            onClick={() => setConfirmation(null)}
          >
            Keep {kind === "enrolment" ? "place" : "wait-list request"}
          </button>
        </div>
      );
    }
    return (
      <button
        type="button"
        className="quiet"
        disabled={busyActivityId !== null}
        aria-label={"Withdraw " + learnerName + (kind === "enrolment" ? " from " : " from the wait-list for ") + title}
        onClick={() => {
          setProblem(null);
          setNotice(null);
          setConfirmation({ activityId, kind });
        }}
      >
        Withdraw
      </button>
    );
  }

  return (
    <main className="group-activities">
      <header>
        <h1>Group activities</h1>
        <p className="muted">
          Browse upcoming Hub activities for {learnerName}. Only your own learner’s enrolments and wait-list requests are shown here.
        </p>
      </header>

      {snapshot.loading && !data && <p role="status">Loading group activities…</p>}
      {snapshot.error && <p role="alert" className="error">{snapshot.error}</p>}
      {problem && <p role="alert" className="error">{problem}</p>}
      {notice && <p role="status">{notice}</p>}

      <section aria-labelledby="group-activity-open-heading">
        <h2 id="group-activity-open-heading">Open activities</h2>
        {data && data.open.length === 0 && <p className="panel">No open activities are available right now.</p>}
        <div className="group-activity-list">
          {data?.open.map((activity) => {
            const enrolled = enrolledActivityIds.has(activity.id);
            const onWaitlist = waitingActivityIds.has(activity.id);
            return (
              <article className="group-activity-card" key={activity.id}>
                <div className="group-activity-copy">
                  <p className="muted">
                    {activityTypeLabel(activity.activityType)} · {displayRange(activity.startsAt, activity.endsAt)}
                  </p>
                  <h3>{activity.title}</h3>
                  {activity.description && <p>{activity.description}</p>}
                  <p>
                    {activity.deliveryMode === "online" ? "Online" : activity.venue}
                    {" · "}
                    {activity.remainingSeats > 0
                      ? activity.remainingSeats + (activity.remainingSeats === 1 ? " place available" : " places available")
                      : "Full"}
                  </p>
                  {activity.eligibilityNotes && (
                    <div className="group-activity-eligibility">
                      <strong>Eligibility information</strong>
                      <p>{activity.eligibilityNotes}</p>
                      <p className="muted">These notes are advisory. This screen does not check eligibility; ask the organiser if you are unsure.</p>
                    </div>
                  )}
                  {enrolled && <p className="group-activity-state" role="status">A place is reserved for {learnerName}.</p>}
                  {onWaitlist && (
                    <p className="group-activity-state" role="status">
                      {activity.waitlistPromotionPolicy === "automatic_reservation"
                        ? learnerName + " is on the wait-list. A place may be reserved automatically if one opens and current authority is confirmed."
                        : learnerName + " is on the wait-list. No place is reserved yet. The organiser may reserve a place for " + learnerName + " later and send you a Hub notification; you can withdraw if you no longer want it."}
                    </p>
                  )}
                </div>
                {!enrolled && !onWaitlist && (
                  <div className="group-activity-actions">
                    {activity.remainingSeats > 0 ? (
                      <button
                        type="button"
                        disabled={busyActivityId !== null}
                        onClick={() => void enrol(activity)}
                        aria-label={"Enrol " + learnerName + " in " + activity.title}
                      >
                        Enrol {learnerName}
                      </button>
                    ) : (
                      <>
                        <p>
                          {activity.waitlistPromotionPolicy === "automatic_reservation"
                            ? "If a place opens, " + learnerName + " will be given the place automatically and enrolled without asking you again."
                            : "Joining does not reserve a place. No learner will be enrolled automatically while the activity's confirmation and communication policy is pending."}
                        </p>
                        <button
                          type="button"
                          disabled={busyActivityId !== null}
                          onClick={() => void joinWaitlist(activity)}
                          aria-label={"Join wait-list for " + learnerName + " in " + activity.title}
                        >
                          Join wait-list
                        </button>
                      </>
                    )}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </section>

      <section aria-labelledby="group-activity-enrolments-heading">
        <h2 id="group-activity-enrolments-heading">Your places</h2>
        {enrolments.length === 0 && data && <p className="panel">No places are reserved for this learner.</p>}
        <div className="group-activity-list">
          {enrolments.map((entry) => (
            <article className="group-activity-card" key={entry.id}>
              <div className="group-activity-copy">
                <p className="muted">{displayRange(entry.startsAt, entry.endsAt)}</p>
                <h3>{entry.activityTitle}</h3>
                <p>A place is reserved for {learnerName}.</p>
              </div>
              {new Date(entry.startsAt).getTime() > Date.now() &&
                renderWithdrawalControls(entry.activityId, "enrolment", entry.activityTitle)}
            </article>
          ))}
        </div>
      </section>

      <section aria-labelledby="group-activity-waitlist-heading">
        <h2 id="group-activity-waitlist-heading">Wait-list requests</h2>
        {waiting.length === 0 && data && <p className="panel">No active wait-list requests for this learner.</p>}
        <div className="group-activity-list">
          {waiting.map((entry) => (
            <article className="group-activity-card" key={entry.id}>
              <div className="group-activity-copy">
                <p className="muted">{displayRange(entry.startsAt, entry.endsAt)}</p>
                <h3>{entry.activityTitle}</h3>
                <p>
                  {entry.promotionPolicy === "automatic_reservation"
                    ? "If a place opens, " + entry.learnerName + " will be given the place automatically and enrolled without asking you again."
                    : entry.learnerName + " will remain on the wait-list. No place is reserved yet. The organiser may reserve a place for " + entry.learnerName + " later and send you a Hub notification; you can withdraw if you no longer want it."}
                  {" "}No participant list is shown.
                </p>
              </div>
              {new Date(entry.startsAt).getTime() > Date.now() &&
                renderWithdrawalControls(entry.activityId, "waitlist", entry.activityTitle)}
            </article>
          ))}
          {invalidated.map((entry) => (
            <article className="group-activity-card" key={entry.id}>
              <div className="group-activity-copy">
                <p className="muted">{displayRange(entry.startsAt, entry.endsAt)}</p>
                <h3>{entry.activityTitle}</h3>
                <p className="group-activity-state" role="status">
                  {entry.learnerName} is no longer on the wait-list. Contact the organiser if you still want a place.
                </p>
              </div>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
}
